import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { 
  Flame, FlaskConical, Pill, Zap, Factory, 
  Wrench, Shield, Settings, ShieldCheck, Truck, Headphones,
  Calendar, Package, Building2, Award, Menu, X, ChevronRight, ChevronDown,
  Phone, Mail, MapPin, Send, CheckCircle2, ArrowRight, HardHat, Star, Users
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { companyInfo, products, industries, certifications, features, stats, clients, productCategories } from '@/mock';

// Industry Card Component - Modern Redesign with Smooth Animations
const IndustryCard = ({ industry, Icon }) => {
  const [isVisible, setIsVisible] = useState(false);
  const cardRef = useRef(null);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.1, rootMargin: '50px' }
    );
    
    if (cardRef.current) {
      observer.observe(cardRef.current);
    }
    
    return () => {
      if (cardRef.current) {
        observer.unobserve(cardRef.current);
      }
    };
  }, []);
  
  return (
    <div
      ref={cardRef}
      className={`group relative transition-all duration-700 ease-out ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
    >
      <Card className="overflow-hidden border-0 shadow-lg hover:shadow-2xl transition-all duration-300 h-full bg-white">
        {/* Image Container */}
        <div className="relative h-64 overflow-hidden">
          <img 
            src={industry.image} 
            alt={`${industry.name} Industry`}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
            loading="lazy"
            decoding="async"
            width="400"
            height="256"
          />
          
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/50 to-slate-900/20 group-hover:from-orange-600/80 group-hover:via-orange-600/50 transition-all duration-500"></div>
          
          {/* Icon - Centered */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="relative">
              <div className="absolute inset-0 bg-white/20 rounded-full blur-xl group-hover:bg-orange-400/30 transition-all duration-500"></div>
              <div className="relative bg-white/90 backdrop-blur-md rounded-full p-5 shadow-2xl group-hover:bg-orange-500 group-hover:scale-110 transition-all duration-500">
                <Icon className="text-slate-800 group-hover:text-white transition-colors duration-300" size={36} strokeWidth={2.5} />
              </div>
            </div>
          </div>
          
          {/* Industry Name - Overlay on Image */}
          <div className="absolute bottom-0 left-0 right-0 p-6 transform translate-y-2 group-hover:translate-y-0 transition-transform duration-300">
            <h3 className="text-2xl font-bold text-white drop-shadow-lg">
              {industry.name}
            </h3>
          </div>
        </div>
        
        {/* Description */}
        <CardContent className="p-6 bg-gradient-to-br from-white to-slate-50/50">
          <p className="text-slate-600 text-sm leading-relaxed group-hover:text-slate-700 transition-colors duration-300">
            {industry.description}
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

const iconMap = {
  Flame, FlaskConical, Pill, Zap, Factory,
  Wrench, Shield, Settings, ShieldCheck, Truck, Headphones,
  Calendar, Package, Building2, Award, HardHat, Star
};

// Structured Data Helper Function - Creates dynamic schemas with current URL
const createStructuredData = () => {
  const currentUrl = typeof window !== 'undefined' ? window.location.origin : 'https://sai-electrical.preview.emergentagent.com';
  
  return {
    organization: {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "SAI INDUSTRIES",
      "alternateName": "SAI-EX",
      "description": "Leading manufacturer of BIS, CIMFR and PESO certified flameproof electrical equipment for hazardous environments in India. Specializing in explosion-proof solutions for Oil & Gas, Chemical, Mining, and Power Generation industries.",
      "url": currentUrl,
      "logo": "https://customer-assets.emergentagent.com/job_sai-electrical/artifacts/8z85qdqz_LOGO.png",
      "image": "https://customer-assets.emergentagent.com/job_sai-electrical/artifacts/8z85qdqz_LOGO.png",
      "foundingDate": "2004",
      "email": "sales@saiex.co.in",
      "telephone": "+91-7490-007-991",
      "priceRange": "$$",
      "currenciesAccepted": "INR",
      "paymentAccepted": "Cash, Credit Card, Bank Transfer",
      "contactPoint": [
        {
          "@type": "ContactPoint",
          "telephone": "+91-7490-007-991",
          "contactType": "Sales",
          "email": "sales@saiex.co.in",
          "areaServed": "IN",
          "availableLanguage": ["en", "hi"]
        },
        {
          "@type": "ContactPoint",
          "telephone": "+91-7490-007-991",
          "contactType": "Customer Service",
          "email": "sales@saiex.co.in",
          "areaServed": "IN",
          "availableLanguage": ["en", "hi"]
        }
      ],
      "sameAs": [],
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Plot No. 4709, PlasticZone, G.I.D.C., Sarlagm",
        "addressLocality": "Valsad",
        "addressRegion": "Gujarat",
        "postalCode": "396155",
        "addressCountry": "IN"
      },
      "areaServed": {
        "@type": "Country",
        "name": "India"
      },
      "makesOffer": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Product",
            "name": "Flameproof Electrical Equipment",
            "description": "BIS, CIMFR & PESO certified flameproof electrical solutions for hazardous areas"
          }
        }
      ],
      "knowsAbout": [
        "Flameproof Electrical Equipment",
        "Explosion-Proof Solutions",
        "Hazardous Area Equipment",
        "Industrial Safety Equipment",
        "BIS Certification",
        "CIMFR Certification",
        "PESO Approval",
        "ISO 9001:2015",
        "ISO 45001:2018",
        "ISO 14001:2015"
      ],
      "additionalType": "https://en.wikipedia.org/wiki/Explosion_protection"
    },
    localBusiness: {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "@id": `${currentUrl}/#localbusiness`,
      "name": "SAI INDUSTRIES",
      "alternateName": "SAI-EX",
      "image": "https://customer-assets.emergentagent.com/job_sai-electrical/artifacts/8z85qdqz_LOGO.png",
      "telephone": "+91-7490-007-991",
      "email": "sales@saiex.co.in",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Plot No. 4709, PlasticZone, G.I.D.C., Sarlagm",
        "addressLocality": "Valsad",
        "addressRegion": "GJ",
        "postalCode": "396155",
        "addressCountry": "IN"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": 20.6101,
        "longitude": 72.9347
      },
      "url": currentUrl,
      "priceRange": "$$",
      "openingHoursSpecification": [
        {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
          "opens": "09:00",
          "closes": "18:00"
        }
      ]
    },
    products: {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "itemListElement": [
        {
          "@type": "Product",
          "name": "Flameproof Junction Box",
          "description": "BIS & CIMFR certified flameproof junction boxes for hazardous areas",
          "brand": {
            "@type": "Brand",
            "name": "SAI-EX"
          },
          "manufacturer": {
            "@type": "Organization",
            "name": "SAI INDUSTRIES"
          }
        },
        {
          "@type": "Product",
          "name": "Flameproof Local Control Station",
          "description": "Certified local control stations for hazardous environments",
          "brand": {
            "@type": "Brand",
            "name": "SAI-EX"
          },
          "manufacturer": {
            "@type": "Organization",
            "name": "SAI INDUSTRIES"
          }
        },
        {
          "@type": "Product",
          "name": "Flameproof Rotary Switch",
          "description": "Industrial-grade rotary switches for explosive atmospheres",
          "brand": {
            "@type": "Brand",
            "name": "SAI-EX"
          },
          "manufacturer": {
            "@type": "Organization",
            "name": "SAI INDUSTRIES"
          }
        }
      ]
    }
  };
};

const Home = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [currentSlide, setCurrentSlide] = useState(0);
  const [productsDropdownOpen, setProductsDropdownOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');

  // Get current URL for dynamic structured data and meta tags
  const currentUrl = typeof window !== 'undefined' ? window.location.origin : '';
  const schemas = createStructuredData();

  const navItems = [
    { id: 'about', label: 'About', type: 'scroll' },
    { id: 'products', label: 'Products', type: 'dropdown' },
    { id: 'industries', label: 'Industries', type: 'scroll' },
    { id: 'certifications', label: 'Certifications', type: 'scroll' },
    { id: 'clients', label: 'Clients', type: 'scroll' },
    { id: 'contact', label: 'Contact', type: 'scroll' }
  ];

  // Industry slideshow images - Highly optimized for speed
  const industrySlides = [
    {
      image: "https://images.unsplash.com/photo-1516937941344-00b4e0337589?w=600&q=50&auto=format&fit=crop",
      title: "Oil & Gas Industry",
      description: "Flameproof solutions for upstream, midstream, and downstream operations"
    },
    {
      image: "https://images.unsplash.com/photo-1546185058-592ead754d27?w=600&q=50&auto=format&fit=crop",
      title: "Chemical & Petrochemical",
      description: "Certified equipment for chemical processing and manufacturing facilities"
    },
    {
      image: "https://customer-assets.emergentagent.com/job_sai-electrical/artifacts/bhimmy3t_mineral-processing-flyability-2.webp",
      title: "Mining Operations",
      description: "Rugged flameproof equipment for harsh underground conditions"
    },
    {
      image: "https://customer-assets.emergentagent.com/job_sai-electrical/artifacts/fkvde1tk_Lighting-Design-Considerations-for-Power-Plants.jpg",
      title: "Power Generation",
      description: "Reliable electrical solutions for power plants and substations"
    }
  ];

  // Auto-rotate slideshow - Optimized timing
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % industrySlides.length);
    }, 5000); // Increased to 5 seconds for smoother transitions

    return () => clearInterval(interval);
  }, [industrySlides.length]);

  // Preload first slideshow image for faster initial render
  useEffect(() => {
    if (industrySlides.length > 0) {
      const img = new Image();
      img.src = industrySlides[0].image;
    }
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const sections = navItems.map(item => item.id);
      const scrollPosition = window.scrollY + 100;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setMobileMenuOpen(false);
      setActiveSection(sectionId);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setFormError(''); // Clear error on input change
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError('');
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to send message');
      }

      const result = await response.json();
      console.log('Form submitted successfully:', result);
      
      setFormSubmitted(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        message: ''
      });
      
      // Reset success message after 5 seconds
      setTimeout(() => {
        setFormSubmitted(false);
      }, 5000);
      
    } catch (error) {
      console.error('Error submitting form:', error);
      setFormError(error.message || 'Failed to send message. Please try again or contact us directly.');
    } finally {
      setFormLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 relative overflow-hidden">
      {/* Enhanced SEO Meta Tags */}
      <Helmet>
        <title>SAI INDUSTRIES - Leading Flameproof Electrical Equipment Manufacturer in India | BIS, CIMFR & PESO Certified</title>
        <meta name="description" content="SAI INDUSTRIES (SAI-EX) - Premier manufacturer of BIS, CIMFR, PESO & ISO 9001:2015 certified flameproof electrical equipment for hazardous areas. Serving Oil & Gas, Chemical, Mining, Power Generation industries across India. Located in Valsad, Gujarat. Contact: +91 7490 007 991" />
        <meta name="keywords" content="flameproof electrical equipment India, explosion proof equipment manufacturer, BIS certified flameproof junction box, CIMFR certified equipment, PESO approved electrical, ISO 9001:2015, hazardous area equipment, flameproof local control station, explosion proof rotary switch, oil gas electrical safety, mining electrical equipment, chemical plant safety equipment, SAI-EX, SAI INDUSTRIES Valsad Gujarat, industrial electrical solutions India, Zone 1 Zone 2 electrical equipment, IIA IIB gas group equipment, flameproof push button station, explosion proof lighting, hazardous location equipment manufacturer India" />
        <meta name="author" content="SAI INDUSTRIES" />
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
        <meta name="googlebot" content="index, follow" />
        <meta name="language" content="English" />
        <meta name="revisit-after" content="7 days" />
        <meta name="distribution" content="global" />
        <meta name="rating" content="general" />
        
        {/* Geographic Meta Tags */}
        <meta name="geo.region" content="IN-GJ" />
        <meta name="geo.placename" content="Valsad, Gujarat, India" />
        <meta name="geo.position" content="20.6101;72.9347" />
        <meta name="ICBM" content="20.6101, 72.9347" />
        
        {/* Business Info */}
        <meta name="contact" content="sales@saiex.co.in" />
        <meta name="coverage" content="India" />
        <meta name="target" content="all" />
        
        <link rel="canonical" href={currentUrl || "https://sai-electrical.preview.emergentagent.com"} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={currentUrl || "https://sai-electrical.preview.emergentagent.com"} />
        <meta property="og:title" content="SAI INDUSTRIES - BIS & CIMFR Certified Flameproof Equipment Manufacturer India" />
        <meta property="og:description" content="Leading manufacturer of BIS, CIMFR & PESO certified flameproof electrical equipment for hazardous environments. Serving Oil & Gas, Chemical, Mining & Power industries across India." />
        <meta property="og:image" content="https://customer-assets.emergentagent.com/job_sai-electrical/artifacts/8z85qdqz_LOGO.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:locale" content="en_IN" />
        <meta property="og:site_name" content="SAI INDUSTRIES" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={currentUrl || "https://sai-electrical.preview.emergentagent.com"} />
        <meta name="twitter:title" content="SAI INDUSTRIES - Flameproof Electrical Equipment India" />
        <meta name="twitter:description" content="BIS, CIMFR & PESO certified flameproof electrical solutions for Oil & Gas, Chemical, Mining industries in India." />
        <meta name="twitter:image" content="https://customer-assets.emergentagent.com/job_sai-electrical/artifacts/8z85qdqz_LOGO.png" />
        
        {/* Structured Data - Organization */}
        <script type="application/ld+json">
          {JSON.stringify(schemas.organization)}
        </script>
        
        {/* Structured Data - LocalBusiness */}
        <script type="application/ld+json">
          {JSON.stringify(schemas.localBusiness)}
        </script>
        
        {/* Structured Data - Products */}
        <script type="application/ld+json">
          {JSON.stringify(schemas.products)}
        </script>
      </Helmet>

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-200 shadow-sm transition-all duration-300">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 lg:h-20">
            {/* Logo */}
            <div className="flex items-center animate-fade-in">
              <img 
                src="https://customer-assets.emergentagent.com/job_sai-electrical/artifacts/8z85qdqz_LOGO.png" 
                alt="SAI-EX - Flameproof Electrical Equipment Manufacturer" 
                className="h-16 lg:h-20 w-auto object-contain hover:scale-105 transition-transform duration-300"
                loading="eager"
              />
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-1" role="navigation" aria-label="Main navigation">
              {navItems.map((item) => (
                item.type === 'dropdown' ? (
                  <div 
                    key={item.id}
                    className="relative group"
                  >
                    <button
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 hover:scale-105 flex items-center gap-1 ${
                        activeSection === item.id
                          ? 'bg-blue-900 text-white shadow-lg'
                          : 'text-slate-700 hover:bg-slate-100'
                      }`}
                    >
                      {item.label}
                      <ChevronDown size={16} className="transition-transform group-hover:rotate-180" />
                    </button>
                    
                    {/* Dropdown - stays open with improved hover area */}
                    <div className="absolute top-full left-0 pt-2 w-80 bg-white rounded-xl shadow-2xl border border-slate-200 py-2 z-50 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 ease-in-out">
                      <Link
                        to="/products"
                        className="block px-4 py-3 hover:bg-orange-50 transition-colors border-b border-slate-100"
                        onClick={() => window.scrollTo(0, 0)}
                      >
                        <div className="font-bold text-orange-600 flex items-center gap-2">
                          <Package size={18} />
                          View All Products
                          <ArrowRight size={14} className="ml-auto" />
                        </div>
                        <div className="text-xs text-slate-600 mt-1">Browse our complete catalog</div>
                      </Link>
                      {productCategories.map(category => (
                        <Link
                          key={category.id}
                          to={`/products?category=${category.slug}`}
                          className="block px-4 py-2 hover:bg-slate-50 transition-colors"
                          onClick={() => window.scrollTo(0, 0)}
                        >
                          <div className="text-sm font-medium text-slate-900">{category.name}</div>
                          <div className="text-xs text-slate-500">{category.description}</div>
                        </Link>
                      ))}
                    </div>
                  </div>
                ) : (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 hover:scale-105 ${
                      activeSection === item.id
                        ? 'bg-blue-900 text-white shadow-lg'
                        : 'text-slate-700 hover:bg-slate-100'
                    }`}
                    aria-label={`Navigate to ${item.label}`}
                  >
                    {item.label}
                  </button>
                )
              ))}
            </nav>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 rounded-lg hover:bg-slate-100 transition-colors"
              aria-label="Toggle mobile menu"
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <nav 
              className="lg:hidden py-4 border-t border-slate-200 animate-slide-down"
              role="navigation"
              aria-label="Mobile navigation"
            >
              {navItems.map((item) => (
                item.type === 'dropdown' ? (
                  <div key={item.id} className="space-y-1">
                    <Link
                      to="/products"
                      className="block w-full text-left px-4 py-3 rounded-lg text-sm font-bold text-orange-600 hover:bg-orange-50 transition-all"
                      onClick={() => {
                        setMobileMenuOpen(false);
                        window.scrollTo(0, 0);
                      }}
                    >
                      {item.label} - View All
                    </Link>
                    {productCategories.map(category => (
                      <Link
                        key={category.id}
                        to={`/products?category=${category.slug}`}
                        className="block w-full text-left px-8 py-2 text-xs text-slate-600 hover:bg-slate-50 transition-all"
                        onClick={() => {
                          setMobileMenuOpen(false);
                          window.scrollTo(0, 0);
                        }}
                      >
                        {category.name}
                      </Link>
                    ))}
                  </div>
                ) : (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className={`block w-full text-left px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                      activeSection === item.id
                        ? 'bg-blue-900 text-white'
                        : 'text-slate-700 hover:bg-slate-100'
                    }`}
                  >
                    {item.label}
                  </button>
                )
              ))}
            </nav>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section 
        id="home" 
        className="pt-24 sm:pt-28 lg:pt-32 pb-12 sm:pb-16 lg:pb-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden min-h-[600px] sm:min-h-[700px] lg:min-h-[800px] flex items-center"
        style={{
          backgroundImage: `url('https://customer-assets.emergentagent.com/job_sai-electrical/artifacts/p2if060v_freepik_create-and-industry-image-for-the-website-home-page-background_0001.jpeg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      >
        {/* Blur overlay */}
        <div className="absolute inset-0 backdrop-blur-sm bg-slate-900/40"></div>
        
        {/* Animated background elements */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-orange-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob"></div>
        <div className="absolute top-40 right-10 w-72 h-72 bg-blue-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob animation-delay-2000"></div>
        
        <div className="container mx-auto relative z-10">
          {/* Single centered content */}
          <div className="max-w-4xl mx-auto text-center">
            <div className="space-y-6 lg:space-y-8">
              {/* Badge with fade-in animation */}
              {/* Company name with slide-up animation */}
              <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-white leading-tight animate-slide-up opacity-0" 
                  style={{ 
                    animationDelay: '0.4s', 
                    animationFillMode: 'forwards',
                    textShadow: '0 4px 20px rgba(0,0,0,0.8), 0 0 40px rgba(249,115,22,0.3)'
                  }}>
                {companyInfo.name}
              </h1>
              
              {/* Tagline with fade-in slide animation */}
              <p className="text-2xl sm:text-3xl lg:text-4xl text-orange-400 font-light animate-fade-in-up opacity-0" 
                 style={{ 
                   animationDelay: '0.6s', 
                   animationFillMode: 'forwards',
                   textShadow: '0 2px 15px rgba(0,0,0,0.8), 0 0 30px rgba(251,146,60,0.4)'
                 }}>
                {companyInfo.tagline}
              </p>
              
              {/* Description with fade-in animation */}
              <p className="text-base sm:text-lg lg:text-xl text-white leading-relaxed max-w-3xl mx-auto animate-fade-in opacity-0" 
                 style={{ 
                   animationDelay: '0.8s', 
                   animationFillMode: 'forwards',
                   textShadow: '0 2px 10px rgba(0,0,0,0.9)'
                 }}>
                Leading Indian manufacturer of certified flameproof electrical equipment for hazardous environments. 
                Committed to safety, quality, and innovation with BIS Approved, CIMFR and PESO Certification, ISO 9001:2015, ISO 45001:2018, ISO 14001:2015, and EIL Enlisted.
              </p>
              
              {/* Buttons with scale-up animation */}
              <div className="flex flex-col sm:flex-row justify-center gap-4 pt-4 animate-fade-in-up opacity-0" 
                   style={{ animationDelay: '1s', animationFillMode: 'forwards' }}>
                <Button 
                  size="lg" 
                  className="bg-gradient-to-r from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800 text-white shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 text-base sm:text-lg px-8 py-6 transform hover:-translate-y-1"
                  onClick={() => scrollToSection('contact')}
                >
                  Get in Touch <ArrowRight className="ml-2" size={20} />
                </Button>
                <Button 
                  size="lg" 
                  variant="outline"
                  onClick={() => scrollToSection('products')}
                  className="border-2 border-white text-white hover:bg-white hover:text-slate-900 transition-all duration-300 hover:scale-105 text-base sm:text-lg px-8 py-6 bg-white/10 backdrop-blur-sm shadow-xl hover:shadow-2xl transform hover:-translate-y-1"
                >
                  View Products
                </Button>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* Industry Applications Slideshow */}
      <section className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-900 to-blue-900 relative overflow-hidden">
        <div className="container mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <Badge className="bg-gradient-to-r from-orange-500 to-orange-600 text-white mb-4 inline-flex border-0">
              Industry Applications
            </Badge>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              Where Flameproof Safety Matters
            </h2>
            <p className="text-base sm:text-lg text-blue-100 max-w-2xl mx-auto">
              Our certified equipment protects workers and operations across hazardous environments
            </p>
          </div>

          {/* Video + Slideshow Layout */}
          <div className="grid lg:grid-cols-2 gap-6 lg:gap-8">
            {/* Left: Industry Video */}
            <div className="order-2 lg:order-1">
              <Card className="border-0 shadow-2xl overflow-hidden bg-slate-800/50 backdrop-blur-sm">
                <div className="relative bg-gradient-to-br from-blue-900 to-slate-800 aspect-video">
                  <iframe
                    className="w-full h-full"
                    src="https://www.youtube.com/embed/YCLRGNID9CY"
                    title="SAI INDUSTRIES - Flameproof Equipment Solutions"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    loading="lazy"
                  ></iframe>
                </div>
                <CardContent className="p-4 sm:p-6 bg-gradient-to-br from-slate-800 to-slate-900">
                  <h3 className="text-lg sm:text-xl font-bold text-white mb-2">Industry Solutions in Action</h3>
                  <p className="text-blue-100 text-sm">
                    See how our flameproof equipment ensures safety across critical industrial applications
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Right: Slideshow */}
            <div className="order-1 lg:order-2">
              <div className="relative h-[400px] sm:h-[500px] lg:h-full min-h-[400px] rounded-2xl overflow-hidden shadow-2xl bg-slate-900">
                {industrySlides.map((slide, index) => (
                  <div
                    key={index}
                    className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
                      index === currentSlide ? 'opacity-100 z-10' : 'opacity-0 z-0'
                    }`}
                  >
                    <img
                      src={slide.image}
                      alt={slide.title}
                      className="w-full h-full object-cover"
                      loading={index === 0 ? "eager" : "lazy"}
                      decoding="async"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/50 to-transparent"></div>
                    
                    {/* Slide Content */}
                    <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8 lg:p-12">
                      <div className={`transition-all duration-700 delay-300 ${
                        index === currentSlide 
                          ? 'opacity-100 translate-y-0' 
                          : 'opacity-0 translate-y-10'
                      }`}>
                        <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-3">
                          {slide.title}
                        </h3>
                        <p className="text-base sm:text-lg text-blue-100 max-w-2xl">
                          {slide.description}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Slide Indicators */}
                <div className="absolute bottom-6 right-6 flex space-x-2 z-20">
                  {industrySlides.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentSlide(index)}
                      className={`w-3 h-3 rounded-full transition-all duration-300 ${
                        index === currentSlide 
                          ? 'bg-orange-500 w-8' 
                          : 'bg-white/50 hover:bg-white/70'
                      }`}
                      aria-label={`Go to slide ${index + 1}`}
                    />
                  ))}
                </div>

                {/* Navigation Arrows */}
                <button
                  onClick={() => setCurrentSlide((prev) => (prev - 1 + industrySlides.length) % industrySlides.length)}
                  className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full p-3 transition-all z-20"
                  aria-label="Previous slide"
                >
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <button
                  onClick={() => setCurrentSlide((prev) => (prev + 1) % industrySlides.length)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full p-3 transition-all z-20"
                  aria-label="Next slide"
                >
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8 flow-gradient-blue">
        <div className="container mx-auto">
          <div className="text-center mb-12 sm:mb-16">
            <Badge className="bg-gradient-to-r from-blue-500 to-blue-600 text-white mb-4 inline-flex border-0">About SAI INDUSTRIES</Badge>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent mb-4">Engineering Excellence Since Decades</h2>
          </div>

          {/* Video Left, Text Right Layout */}
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 mb-12 sm:mb-16">
            {/* Left: Video */}
            <div className="order-2 lg:order-1">
              <Card className="border-0 shadow-2xl overflow-hidden">
                <div className="relative bg-gradient-to-br from-blue-900 to-slate-800 aspect-video">
                  <iframe
                    className="w-full h-full"
                    src="https://www.youtube.com/embed/elYyLEYAWn8"
                    title="About SAI INDUSTRIES - Company Overview"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    loading="lazy"
                  ></iframe>
                </div>
                <CardContent className="p-4 sm:p-6">
                  <div className="grid grid-cols-3 gap-3 sm:gap-4">
                    <div className="text-center p-2 sm:p-3 bg-blue-50 rounded-lg">
                      <div className="text-xl sm:text-2xl font-bold text-orange-600 mb-1">36+</div>
                      <div className="text-xs text-slate-700">Years</div>
                    </div>
                    <div className="text-center p-2 sm:p-3 bg-orange-50 rounded-lg">
                      <div className="text-xl sm:text-2xl font-bold text-blue-600 mb-1">18500+</div>
                      <div className="text-xs text-slate-700">Orders</div>
                    </div>
                    <div className="text-center p-2 sm:p-3 bg-blue-50 rounded-lg">
                      <div className="text-xl sm:text-2xl font-bold text-orange-600 mb-1">100+</div>
                      <div className="text-xs text-slate-700">Industrial Clients</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Right: Text Content */}
            <div className="order-1 lg:order-2 flex flex-col justify-center">
              <div className="space-y-6">
                <p className="text-base sm:text-lg text-slate-700 leading-relaxed">
                  <span className="text-2xl font-bold text-orange-600 block mb-2">Who We Are</span>
                  SAI Industries is a leading manufacturer of flameproof electrical equipment designed for hazardous environments. 
                  Located in Valsad, Gujarat, we specialize in providing certified solutions for industries operating in potentially 
                  explosive atmospheres including Oil & Gas, Chemical, Petrochemical, Pharmaceutical, and Mining sectors.
                </p>
                
                <p className="text-base sm:text-lg text-slate-700 leading-relaxed">
                  <span className="text-2xl font-bold text-blue-600 block mb-2">Our Commitment</span>
                  Our products are manufactured to the highest Indian and international standards, certified by BIS, CIMFR, PESO, EIL, ISO 9001:2015, ISO 45001:2018, and ISO 14001:2015. 
                  We offer customized solutions tailored to meet specific industrial requirements with comprehensive technical support.
                </p>

                <div className="grid grid-cols-2 gap-4 pt-4">
                  <div className="flex items-start space-x-3">
                    <CheckCircle2 className="text-green-600 flex-shrink-0 mt-1" size={20} />
                    <div>
                      <h4 className="font-semibold text-slate-900">Quality Assured</h4>
                      <p className="text-sm text-slate-600">BIS, CIMFR, PESO certified</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle2 className="text-green-600 flex-shrink-0 mt-1" size={20} />
                    <div>
                      <h4 className="font-semibold text-slate-900">Custom Solutions</h4>
                      <p className="text-sm text-slate-600">Tailored to your needs</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle2 className="text-green-600 flex-shrink-0 mt-1" size={20} />
                    <div>
                      <h4 className="font-semibold text-slate-900">Expert Support</h4>
                      <p className="text-sm text-slate-600">Technical assistance</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle2 className="text-green-600 flex-shrink-0 mt-1" size={20} />
                    <div>
                      <h4 className="font-semibold text-slate-900">Trusted Partner</h4>
                      <p className="text-sm text-slate-600">15+ major PSUs</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Compact Feature Tabs - Modern Design */}
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
              {features.map((feature, index) => {
                const Icon = iconMap[feature.icon];
                return (
                  <div 
                    key={feature.id} 
                    className="group relative overflow-hidden rounded-xl bg-gradient-to-br from-white via-slate-50 to-blue-50 border-2 border-slate-200 hover:border-orange-400 transition-all duration-300 hover:scale-105 hover:shadow-2xl cursor-pointer animate-fade-in-up"
                    style={{ animationDelay: `${index * 50}ms` }}
                  >
                    {/* Gradient Overlay on Hover */}
                    <div className="absolute inset-0 bg-gradient-to-br from-orange-500/0 to-blue-500/0 group-hover:from-orange-500/10 group-hover:to-blue-500/10 transition-all duration-300"></div>
                    
                    <div className="relative p-4 sm:p-5 text-center">
                      {/* Icon */}
                      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br from-orange-100 to-orange-200 flex items-center justify-center mx-auto mb-3 group-hover:from-orange-500 group-hover:to-orange-600 transition-all duration-300 shadow-md group-hover:shadow-xl">
                        <Icon className="text-orange-600 group-hover:text-white transition-colors duration-300" size={20} />
                      </div>
                      
                      {/* Title */}
                      <h3 className="text-sm sm:text-base font-bold text-slate-900 mb-2 group-hover:text-orange-600 transition-colors">
                        {feature.title}
                      </h3>
                      
                      {/* Description */}
                      <p className="text-xs sm:text-sm text-slate-600 leading-snug">
                        {feature.description}
                      </p>
                    </div>
                    
                    {/* Decorative Corner */}
                    <div className="absolute -top-2 -right-2 w-16 h-16 bg-gradient-to-br from-orange-400/20 to-blue-400/20 rounded-full blur-xl group-hover:scale-150 transition-transform duration-500"></div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Products Section - Photo Gallery */}
      <section id="products-gallery" className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="container mx-auto">
          <div className="text-center mb-12 sm:mb-16">
            <Badge className="bg-gradient-to-r from-orange-500 to-orange-600 text-white mb-4 inline-flex border-0">
              Our Products
            </Badge>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent mb-4">
              Flameproof Electrical Equipment
            </h2>
            <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto">
              Certified products for hazardous environments across industries
            </p>
          </div>

          {/* Product Grid - Photos and Names Only */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
            {products.map((product, index) => (
              <div 
                key={product.id}
                className="group cursor-pointer animate-fade-in-up"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="relative overflow-hidden rounded-xl bg-white shadow-lg hover:shadow-2xl transition-all duration-500 border border-slate-200 hover:border-orange-400">
                  {/* Product Image */}
                  <div className="relative aspect-square overflow-hidden bg-white">
                    <img 
                      src={product.image} 
                      alt={product.name}
                      className="w-full h-full object-contain p-4 group-hover:scale-105 transition-transform duration-300"
                      loading="lazy"
                      decoding="async"
                      width="300"
                      height="300"
                    />
                    {/* SAI-Ex Brand Watermark */}
                    <img
                      src="https://customer-assets.emergentagent.com/job_sai-electrical/artifacts/51rvn1xg_FAVCON1.png"
                      alt="SAI-Ex Brand"
                      aria-hidden="true"
                      className="absolute bottom-2 right-2 w-12 sm:w-14 lg:w-16 h-auto opacity-60 mix-blend-multiply pointer-events-none select-none drop-shadow-sm"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                  
                  {/* Product Name - Light Background Below Image */}
                  <div className="bg-gradient-to-r from-white to-slate-50 p-4 border-t-2 border-orange-400">
                    <div className="flex justify-center mb-2">
                      <span className="sai-ex-branded-badge">SAI-Ex Branded</span>
                    </div>
                    <h3 className="text-sm sm:text-base font-bold text-slate-900 text-center leading-tight group-hover:text-orange-600 transition-colors duration-300">
                      {product.name}
                    </h3>
                    <div className="h-1 w-12 bg-gradient-to-r from-orange-500 to-blue-500 mx-auto mt-2 rounded-full group-hover:w-full transition-all duration-300"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* View All Products Button */}
          <div className="text-center mt-12">
            <Link to="/products">
              <Button 
                size="lg" 
                className="bg-gradient-to-r from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800 text-white shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 text-base sm:text-lg px-8 py-6"
                onClick={() => window.scrollTo(0, 0)}
              >
                View All Products <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <section id="industries" className="py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-50 via-white to-blue-50 relative overflow-hidden">
        {/* Simple Clean Animated Background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Animated Gradient Orbs Only */}
          <div className="absolute top-20 left-10 w-96 h-96 bg-gradient-to-br from-orange-300/30 to-pink-300/30 rounded-full mix-blend-multiply filter blur-3xl animate-blob"></div>
          <div className="absolute top-40 right-10 w-96 h-96 bg-gradient-to-br from-blue-300/30 to-cyan-300/30 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-2000"></div>
          <div className="absolute bottom-20 left-1/3 w-96 h-96 bg-gradient-to-br from-purple-300/30 to-indigo-300/30 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-4000"></div>
        </div>
        
        <div className="container mx-auto relative z-10">
          <div className="text-center mb-16 space-y-4">
            <Badge className="bg-gradient-to-r from-blue-600 to-blue-700 text-white mb-4 inline-flex border-0 shadow-lg">
              Industries Served
            </Badge>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 bg-clip-text text-transparent">
              Diverse Applications
            </h2>
            <p className="text-lg sm:text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
              Trusted by leading companies across multiple industries worldwide
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 max-w-7xl mx-auto">
            {industries.map((industry) => {
              const Icon = iconMap[industry.icon];
              return (
                <IndustryCard 
                  key={industry.id}
                  industry={industry}
                  Icon={Icon}
                />
              );
            })}
          </div>
        </div>
      </section>

      {/* Certifications Section */}
      <section id="certifications" className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-900 to-slate-800 text-white">
        <div className="container mx-auto">
          <div className="text-center mb-12 sm:mb-16">
            <Badge className="bg-white/20 text-white mb-4 border-0 inline-flex">Indian Certifications</Badge>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">Quality and Compliance</h2>
            <p className="text-base sm:text-lg text-blue-100 max-w-2xl mx-auto">
              Our products meet the highest Indian and international standards for safety and quality.
            </p>
          </div>

          {/* Horizontal One-Line Display with Circular Logos */}
          <div className="flex flex-wrap justify-center items-center gap-6 sm:gap-8 lg:gap-12">
            {certifications.map((cert, index) => (
              <div 
                key={cert.id}
                className="flex flex-col items-center group"
              >
                {/* Circular Logo Container - WHITE like clients */}
                <div className="relative mb-4">
                  <div className="w-40 h-40 sm:w-48 sm:h-48 lg:w-56 lg:h-56 rounded-full bg-white shadow-2xl flex items-center justify-center p-6 sm:p-7 lg:p-8 group-hover:scale-105 transition-transform duration-300 border-4 border-slate-100 group-hover:border-orange-300 overflow-hidden">
                    
                    {cert.logo && (
                      <img 
                        src={cert.logo} 
                        alt={`${cert.name} Logo`}
                        className={`relative z-10 w-full h-full object-contain ${cert.name === 'GeM Registered' ? 'scale-[1.45]' : 'client-logo-clean'}`}
                        loading="lazy"
                        decoding="async"
                        width="224"
                        height="224"
                      />
                    )}
                  </div>
                </div>
                
                {/* Certification Name - LARGER TEXT */}
                <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-white text-center max-w-[180px] leading-tight">
                  {cert.name}
                </h3>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <p className="text-blue-100 text-sm sm:text-base">
              Trusted by leading industries across India for over 25 years
            </p>
          </div>
        </div>
      </section>

      {/* Clients Section - Infinite Scroll Marquee */}
      <section id="clients" className="py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-50 via-white to-blue-50 overflow-hidden">
        <div className="container mx-auto">
          <div className="text-center mb-12 sm:mb-16">
            <Badge className="bg-gradient-to-r from-blue-600 to-blue-700 text-white mb-4 inline-flex border-0">
              Trusted Partners
            </Badge>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent mb-4">
              Our Esteemed Clients
            </h2>
            <p className="text-base sm:text-lg text-slate-600 max-w-3xl mx-auto">
              Proud to serve India's leading organizations in defense, power, and critical infrastructure
            </p>
          </div>

          {/* All Clients Grid - Attractive Layout */}
          <div className="max-w-7xl mx-auto">
            {/* Flex-wrap with center justification so last-row orphan logos stay centered */}
            <div className="flex flex-wrap justify-center gap-6 sm:gap-8 lg:gap-10">
              {clients.map((client, index) => (
                <div
                  key={client.id}
                  className="flex flex-col items-center group basis-[calc((100%-3rem)/3)] sm:basis-[calc((100%-6rem)/4)] lg:basis-[calc((100%-10rem)/5)] max-w-[12rem]"
                >
                  {/* Circular Container - Simplified */}
                  <div className="relative w-32 h-32 sm:w-40 sm:h-40 lg:w-48 lg:h-48 flex items-center justify-center bg-white rounded-full shadow-xl hover:shadow-2xl transition-shadow duration-300 overflow-visible border-4 border-slate-100 group-hover:border-orange-300">
                    
                    {/* Logo - Always Colorful */}
                    <img
                      src={client.logo}
                      alt={`${client.name} - SAI INDUSTRIES Client`}
                      className="relative z-10 w-24 h-24 sm:w-32 sm:h-32 lg:w-40 lg:h-40 object-contain p-4 transition-transform duration-300 group-hover:scale-110 client-logo-clean"
                      loading="lazy"
                      decoding="async"
                      width="160"
                      height="160"
                    />
                    
                    {/* Enhanced Tooltip - Above Logo */}
                    <div className="absolute -top-12 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-50 pointer-events-none">
                      <div className="bg-orange-600 text-white px-3 py-1.5 rounded-lg shadow-lg whitespace-nowrap text-xs sm:text-sm font-bold">
                        {client.name}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Additional Trust Indicators */}
          <div className="mt-12 text-center">
            <p className="text-slate-600 text-sm sm:text-base italic">
              "Delivering excellence to India's most trusted organizations since decades"
            </p>
          </div>
        </div>
      </section>


      {/* SEO-Optimized FAQ Section */}
      <section id="faq" className="py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-12">
            <Badge className="bg-blue-600 text-white mb-4 inline-flex">Frequently Asked Questions</Badge>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
              Everything About Flameproof Electrical Equipment
            </h2>
            <p className="text-lg text-slate-600">
              Common questions about BIS, CIMFR, and PESO certified flameproof solutions
            </p>
          </div>

          <div className="space-y-4">
            {/* FAQ 1 */}
            <details className="group bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden">
              <summary className="cursor-pointer p-6 font-bold text-lg text-slate-900 flex justify-between items-center list-none">
                <span>What is flameproof electrical equipment and why is it important?</span>
                <ChevronDown className="group-open:rotate-180 transition-transform" />
              </summary>
              <div className="px-6 pb-6 text-slate-600 leading-relaxed">
                <p>Flameproof electrical equipment is specially designed and certified equipment that can contain any explosion within its enclosure, preventing ignition of surrounding flammable gases or vapors. This is crucial for hazardous areas in industries like oil & gas, chemical plants, pharmaceuticals, and mining where explosive atmospheres may be present.</p>
                <p className="mt-3">SAI INDUSTRIES manufactures BIS, CIMFR, and PESO certified flameproof equipment that meets international standards for Zone 1 and Zone 2 hazardous areas, ensuring maximum safety for your operations.</p>
              </div>
            </details>

            {/* FAQ 2 */}
            <details className="group bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden">
              <summary className="cursor-pointer p-6 font-bold text-lg text-slate-900 flex justify-between items-center list-none">
                <span>What certifications does SAI INDUSTRIES have for flameproof equipment?</span>
                <ChevronDown className="group-open:rotate-180 transition-transform" />
              </summary>
              <div className="px-6 pb-6 text-slate-600 leading-relaxed">
                <p>SAI INDUSTRIES holds all major certifications required for flameproof electrical equipment manufacturing in India:</p>
                <ul className="mt-3 space-y-2 list-disc list-inside">
                  <li><strong>BIS Certification</strong> - Bureau of Indian Standards approval for quality compliance</li>
                  <li><strong>CIMFR Certification</strong> - Central Institute of Mining & Fuel Research certification for mining safety</li>
                  <li><strong>PESO Certification</strong> - Petroleum and Explosives Safety Organisation approval</li>
                  <li><strong>ISO 9001:2015</strong> - Quality Management System</li>
                  <li><strong>ISO 45001:2018</strong> - Occupational Health & Safety Management</li>
                  <li><strong>ISO 14001:2015</strong> - Environmental Management System</li>
                  <li><strong>EIL Enlisted</strong> - Engineers India Limited approved vendor</li>
                </ul>
              </div>
            </details>

            {/* FAQ 3 */}
            <details className="group bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden">
              <summary className="cursor-pointer p-6 font-bold text-lg text-slate-900 flex justify-between items-center list-none">
                <span>Which industries use flameproof electrical equipment from SAI INDUSTRIES?</span>
                <ChevronDown className="group-open:rotate-180 transition-transform" />
              </summary>
              <div className="px-6 pb-6 text-slate-600 leading-relaxed">
                <p>Our PESO and CIMFR certified flameproof equipment is trusted across multiple hazardous industries:</p>
                <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <li className="flex items-center gap-2"><span className="text-orange-600">✓</span> Oil & Gas Refineries</li>
                  <li className="flex items-center gap-2"><span className="text-orange-600">✓</span> Chemical & Petrochemical Plants</li>
                  <li className="flex items-center gap-2"><span className="text-orange-600">✓</span> Pharmaceutical Manufacturing</li>
                  <li className="flex items-center gap-2"><span className="text-orange-600">✓</span> Underground Mining Operations</li>
                  <li className="flex items-center gap-2"><span className="text-orange-600">✓</span> Power Generation Plants</li>
                  <li className="flex items-center gap-2"><span className="text-orange-600">✓</span> Manufacturing Facilities</li>
                </ul>
                <p className="mt-3">Major clients include IOCL, ONGC, Reliance, GAIL, and other leading Indian PSUs.</p>
              </div>
            </details>

            {/* FAQ 4 */}
            <details className="group bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden">
              <summary className="cursor-pointer p-6 font-bold text-lg text-slate-900 flex justify-between items-center list-none">
                <span>What types of flameproof products does SAI INDUSTRIES manufacture?</span>
                <ChevronDown className="group-open:rotate-180 transition-transform" />
              </summary>
              <div className="px-6 pb-6 text-slate-600 leading-relaxed">
                <p>We manufacture a comprehensive range of BIS and CIMFR certified flameproof electrical equipment:</p>
                <ul className="mt-3 space-y-2">
                  <li><strong>Flameproof Junction Boxes</strong> - Terminal and junction boxes for various applications</li>
                  <li><strong>Flameproof Lighting Solutions</strong> - LED floodlights, well glass lights, tube fittings</li>
                  <li><strong>Flameproof Control Stations</strong> - Push button stations, control panels, local control stations</li>
                  <li><strong>Flameproof Switches & Sockets</strong> - Explosion-proof switches and electrical connections</li>
                  <li><strong>Flameproof Ventilation Equipment</strong> - Fans for hazardous area ventilation</li>
                  <li><strong>Flameproof Motor Starters</strong> - DOL and Star Delta starters up to 40 HP</li>
                </ul>
              </div>
            </details>

            {/* FAQ 5 */}
            <details className="group bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden">
              <summary className="cursor-pointer p-6 font-bold text-lg text-slate-900 flex justify-between items-center list-none">
                <span>How do I choose the right flameproof equipment for my facility?</span>
                <ChevronDown className="group-open:rotate-180 transition-transform" />
              </summary>
              <div className="px-6 pb-6 text-slate-600 leading-relaxed">
                <p>Selecting appropriate flameproof electrical equipment depends on several factors:</p>
                <ol className="mt-3 space-y-3 list-decimal list-inside">
                  <li><strong>Zone Classification</strong> - Identify if your area is Zone 0, Zone 1, or Zone 2</li>
                  <li><strong>Gas Group</strong> - Determine the gas group classification (IIA, IIB, IIC)</li>
                  <li><strong>Temperature Class</strong> - Check the maximum surface temperature requirements (T1-T6)</li>
                  <li><strong>Required Certifications</strong> - Ensure BIS, CIMFR, or PESO certification as needed</li>
                  <li><strong>Application Requirements</strong> - Consider power ratings, environmental conditions, and mounting needs</li>
                </ol>
                <p className="mt-3">Our technical team at SAI INDUSTRIES provides free consultation to help you select PESO certified flameproof equipment that meets all safety standards and regulations.</p>
              </div>
            </details>

            {/* FAQ 6 */}
            <details className="group bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden">
              <summary className="cursor-pointer p-6 font-bold text-lg text-slate-900 flex justify-between items-center list-none">
                <span>What is the difference between flameproof and explosion-proof equipment?</span>
                <ChevronDown className="group-open:rotate-180 transition-transform" />
              </summary>
              <div className="px-6 pb-6 text-slate-600 leading-relaxed">
                <p>While often used interchangeably, there are technical differences:</p>
                <p className="mt-3"><strong>Flameproof (Ex d)</strong> - Equipment designed to contain an internal explosion and prevent external ignition. This is the IEC/European standard terminology used in India with BIS and CIMFR certifications.</p>
                <p className="mt-3"><strong>Explosion-proof</strong> - Similar concept but refers to North American (NEC/CEC) standards. The equipment contains explosions and prevents hazard propagation.</p>
                <p className="mt-3">SAI INDUSTRIES manufactures flameproof equipment according to IEC standards with BIS certification, which is mandatory for Indian hazardous area installations.</p>
              </div>
            </details>
          </div>
        </div>
      </section>

      {/* Why Choose SAI INDUSTRIES - USP Section */}
      <section className="py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <Badge className="bg-orange-600 text-white mb-4 inline-flex">Why Choose Us</Badge>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
              Leading Flameproof Equipment Manufacturer in India
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              Trusted by India's top PSUs for certified flameproof electrical solutions
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* USP 1 */}
            <div className="bg-gradient-to-br from-blue-50 to-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-blue-100">
              <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mb-6">
                <Shield className="text-white" size={32} />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Triple Certified Equipment</h3>
              <p className="text-slate-600 leading-relaxed">
                All our flameproof products carry BIS, CIMFR, and PESO certifications ensuring compliance with Indian safety standards and regulations for hazardous area installations.
              </p>
            </div>

            {/* USP 2 */}
            <div className="bg-gradient-to-br from-orange-50 to-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-orange-100">
              <div className="w-16 h-16 bg-orange-600 rounded-full flex items-center justify-center mb-6">
                <Factory className="text-white" size={32} />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Made in India Manufacturing</h3>
              <p className="text-slate-600 leading-relaxed">
                Indigenous manufacturing facility in Valsad, Gujarat with ISO 9001:2015 certified quality processes. Supporting Atmanirbhar Bharat with world-class flameproof solutions.
              </p>
            </div>

            {/* USP 3 */}
            <div className="bg-gradient-to-br from-green-50 to-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-green-100">
              <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mb-6">
                <Users className="text-white" size={32} />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Trusted by Top PSUs</h3>
              <p className="text-slate-600 leading-relaxed">
                Preferred vendor for IOCL, ONGC, BPCL, Reliance, GAIL, and other major Indian oil, gas, and petrochemical companies for CIMFR certified flameproof equipment.
              </p>
            </div>

            {/* USP 4 */}
            <div className="bg-gradient-to-br from-purple-50 to-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-purple-100">
              <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mb-6">
                <Award className="text-white" size={32} />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">ISO Certified Quality</h3>
              <p className="text-slate-600 leading-relaxed">
                ISO 9001:2015, ISO 45001:2018, and ISO 14001:2015 certified ensuring quality management, occupational health & safety, and environmental compliance.
              </p>
            </div>

            {/* USP 5 */}
            <div className="bg-gradient-to-br from-red-50 to-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-red-100">
              <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mb-6">
                <Zap className="text-white" size={32} />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Comprehensive Product Range</h3>
              <p className="text-slate-600 leading-relaxed">
                Complete portfolio of PESO approved flameproof junction boxes, lighting, control stations, switches, ventilation equipment, and motor starters for all hazardous areas.
              </p>
            </div>

            {/* USP 6 */}
            <div className="bg-gradient-to-br from-indigo-50 to-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-indigo-100">
              <div className="w-16 h-16 bg-indigo-600 rounded-full flex items-center justify-center mb-6">
                <Phone className="text-white" size={32} />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Expert Technical Support</h3>
              <p className="text-slate-600 leading-relaxed">
                Dedicated team providing consultation on zone classification, equipment selection, installation guidance, and compliance with BIS standards for hazardous area electrical installations.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Service Areas - Local SEO */}
      <section className="py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-900 to-blue-900 text-white">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <Badge className="bg-white text-slate-900 mb-4 inline-flex">Service Areas</Badge>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
              Flameproof Equipment Supplier Across India
            </h2>
            <p className="text-lg text-blue-100 max-w-3xl mx-auto">
              Delivering BIS and CIMFR certified flameproof solutions pan-India
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 max-w-5xl mx-auto">
            {[
              { state: 'Gujarat', cities: 'Valsad, Ahmedabad, Surat, Vadodara' },
              { state: 'Maharashtra', cities: 'Mumbai, Pune, Nagpur, Nashik' },
              { state: 'Delhi NCR', cities: 'Delhi, Gurgaon, Noida, Faridabad' },
              { state: 'Uttar Pradesh', cities: 'Lucknow, Kanpur, Agra, Varanasi' },
              { state: 'Rajasthan', cities: 'Jaipur, Jodhpur, Udaipur, Kota' },
              { state: 'Tamil Nadu', cities: 'Chennai, Coimbatore, Madurai' },
              { state: 'Karnataka', cities: 'Bangalore, Mysore, Mangalore' },
              { state: 'Andhra Pradesh', cities: 'Visakhapatnam, Vijayawada' },
            ].map((area, index) => (
              <div key={index} className="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300 border border-white/20">
                <h3 className="font-bold text-lg mb-2">{area.state}</h3>
                <p className="text-sm text-blue-100">{area.cities}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <p className="text-blue-100 text-lg mb-4">
              <strong>Manufacturing Location:</strong> Plot No. 4709, Plastic Zone, G.I.D.C., Sarigam, Valsad, Gujarat - 396155
            </p>
            <p className="text-blue-100">
              Supplying PESO certified flameproof electrical equipment to refineries, chemical plants, mining operations, and hazardous area installations across all Indian states and union territories.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8 animated-gradient-bg-alt">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12 sm:mb-16">
            <Badge className="bg-orange-100 text-orange-700 mb-4 inline-flex">Get in Touch</Badge>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4">Contact Us</h2>
            <p className="text-base sm:text-lg text-slate-600">
              Have a project or inquiry? We'd love to hear from you.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 sm:gap-12">
            {/* Contact Info */}
            <div className="space-y-6 sm:space-y-8">
              <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className="p-4 sm:p-6 space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 rounded-lg bg-orange-100 flex items-center justify-center flex-shrink-0">
                      <Phone className="text-orange-600" size={24} />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-1">Phone</h4>
                      <a href={`tel:${companyInfo.phone}`} className="text-slate-600 hover:text-orange-600 transition-colors block">
                        {companyInfo.phone}
                      </a>
                      <a href={`tel:${companyInfo.phone2}`} className="text-slate-600 hover:text-orange-600 transition-colors block">
                        {companyInfo.phone2}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 rounded-lg bg-orange-100 flex items-center justify-center flex-shrink-0">
                      <Mail className="text-orange-600" size={24} />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-1">Email</h4>
                      <a href={`mailto:${companyInfo.email}`} className="text-slate-600 hover:text-orange-600 transition-colors break-all">
                        {companyInfo.email}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 rounded-lg bg-orange-100 flex items-center justify-center flex-shrink-0">
                      <MapPin className="text-orange-600" size={24} />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-1">Address</h4>
                      <a 
                        href="https://www.google.com/maps/search/?api=1&query=Plot+No.+4709,+Plastic+Zone,+G.I.D.C.,+Sarigam,+Valsad,+Gujarat-396155"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-slate-600 hover:text-orange-600 transition-colors"
                      >
                        {companyInfo.address}
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg bg-gradient-to-br from-orange-600 to-orange-700 text-white hover:shadow-xl transition-shadow">
                <CardContent className="p-6 sm:p-8">
                  <h3 className="text-xl sm:text-2xl font-bold mb-4">Need a Custom Solution?</h3>
                  <p className="mb-6 text-orange-50 text-sm sm:text-base">
                    Our engineering team can design and manufacture custom flameproof electrical equipment tailored to your specific requirements.
                  </p>
                  <Button 
                    variant="secondary" 
                    className="bg-white text-orange-600 hover:bg-orange-50 w-full sm:w-auto"
                    onClick={() => scrollToSection('contact')}
                  >
                    Request Consultation <ChevronRight size={18} />
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* Contact Form */}
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="p-6 sm:p-8">
                {formSubmitted ? (
                  <div className="text-center py-12 animate-fade-in">
                    <CheckCircle2 size={64} className="text-green-600 mx-auto mb-4 animate-pulse-slow" />
                    <h3 className="text-2xl font-bold text-slate-900 mb-2">Thank You!</h3>
                    <p className="text-slate-600">We'll get back to you shortly.</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Name *</label>
                      <Input
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        placeholder="Your name"
                        className="border-slate-300 focus:border-orange-500 focus:ring-orange-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Email *</label>
                      <Input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        placeholder="your@email.com"
                        className="border-slate-300 focus:border-orange-500 focus:ring-orange-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Phone</label>
                      <Input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="+91 XXX XXX XXXX"
                        className="border-slate-300 focus:border-orange-500 focus:ring-orange-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Company</label>
                      <Input
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        placeholder="Your company name"
                        className="border-slate-300 focus:border-orange-500 focus:ring-orange-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Message *</label>
                      <Textarea
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        required
                        rows={4}
                        placeholder="Tell us about your requirements..."
                        className="border-slate-300 focus:border-orange-500 focus:ring-orange-500"
                      />
                    </div>
                    
                    {formError && (
                      <div className="p-4 bg-red-50 border border-red-200 rounded-lg animate-fade-in">
                        <p className="text-red-600 text-sm flex items-center gap-2">
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                          {formError}
                        </p>
                      </div>
                    )}
                    
                    <Button 
                      type="submit" 
                      className="w-full bg-orange-600 hover:bg-orange-700 text-white hover:scale-105 transition-transform disabled:opacity-50 disabled:cursor-not-allowed"
                      size="lg"
                      disabled={formLoading}
                    >
                      {formLoading ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Sending...
                        </>
                      ) : (
                        <>
                          Send Message <Send className="ml-2" size={18} />
                        </>
                      )}
                    </Button>
                  </form>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
      {/* Footer */}
      <footer className="bg-slate-900 text-white py-8 sm:py-12 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto">
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8 mb-6 sm:mb-8">
            <div>
              <img 
                src="https://customer-assets.emergentagent.com/job_sai-electrical/artifacts/8z85qdqz_LOGO.png" 
                alt="SAI-EX Logo" 
                className="h-14 sm:h-16 w-auto object-contain brightness-0 invert mb-4"
                loading="lazy"
              />
              <p className="text-slate-400 text-sm">
                Leading manufacturer of certified flameproof electrical equipment for hazardous environments.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-base sm:text-lg">Quick Links</h4>
              <ul className="space-y-2 text-sm">
                {navItems.slice(1).map((item) => (
                  <li key={item.id}>
                    <button 
                      onClick={() => scrollToSection(item.id)}
                      className="text-slate-400 hover:text-white transition-colors"
                    >
                      {item.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-base sm:text-lg">Contact Info</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li>
                  <a href={`mailto:${companyInfo.email}`} className="hover:text-white transition-colors">
                    {companyInfo.email}
                  </a>
                </li>
                <li>
                  <a href={`tel:${companyInfo.phone}`} className="hover:text-white transition-colors">
                    {companyInfo.phone}
                  </a>
                </li>
                <li>
                  <a href={`tel:${companyInfo.phone2}`} className="hover:text-white transition-colors">
                    {companyInfo.phone2}
                  </a>
                </li>
                <li>{companyInfo.address}</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-6 sm:pt-8 text-center text-xs sm:text-sm text-slate-400">
            <p>&copy; {new Date().getFullYear()} SAI INDUSTRIES. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
